import {
  Field,
  Label,
  Menu,
  MenuItem,
  MenuItems,
  MenuSection,
  Switch,
} from "@headlessui/react";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import User from "../../classes/user";
import LanguageSelectionMenu from "../components/local/LanguageSelectionMenu";
import useBreakpoint from "../utils/use-breakpoint";
import {
  getUserSettingsByUsername,
  postUserSettingsByUsername,
} from "../utils/user-settings";
import { environment } from "../config";
import { getBrandOptionByDomain, getSelectedBrandFromCookie } from "../utils/brand-selector";
import DebugModal from "../components/debug-modal";
import { usePortalNotificationContext } from "../components/notifications/context/PortalNotificationProvider";
import { PATH_NOTIFICATIONS } from "../config/path-config";

type ProfileMenuProps = {
  user: User;
  logout: () => void;
  menuButton: React.ReactNode;
  inSidebar?: boolean; // Add prop to indicate sidebar context
};

const ProfileMenu = ({ user, logout, menuButton, inSidebar = false }: ProfileMenuProps) => {
  const breakpoint = useBreakpoint();
  const isDesktop = ["xl", "2xl"].includes(breakpoint);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { totalUnRead } = usePortalNotificationContext();
  const [userSettings, setUserSettings] = useState({ dark_mode: false });
  const [debugModalOpen, setDebugModalOpen] = useState(false);


  // Get current brand for display
  const currentBrandDomain = getSelectedBrandFromCookie();
  const currentBrand = currentBrandDomain ? getBrandOptionByDomain(currentBrandDomain) : null;

  useEffect(() => {
    const fetchSettings = async () => {
      const currentSettings = await getUserSettingsByUsername(user.sf_user_id);
      setUserSettings(currentSettings);
    };
    fetchSettings();
  }, [user]);

  return (
    <Menu as="div" className="relative">
      <MenuSection>
        <div>{menuButton}</div>
        <MenuItems
          className={clsx(
            "absolute z-50 w-48 rounded-md bg-white dark:bg-gray-900 shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in overflow-visible",
            inSidebar || isDesktop ? "bottom-12 right-2" : "top-9 right-0"
          )}
        >
          <div className="animate-fade-in-fast p-2">
            <MenuItem>
              {({ focus }) => (
                <Link
                  to={"users/" + user.sf_user_id}
                  className={clsx(
                    "flex items-center justify-between py-3 px-2 rounded-md w-full text-sm font-medium hover:bg-gray-100 cursor-pointer",
                    focus ? "bg-gray-100" : "dark:text-white"
                  )}
                >
                  {t("user_page_menu_profile")}
                  {/* TBD: (MOBILE VIEW) do we want to show this on focus only? */}
                  {/* {focus && <span className="icon-[ic--round-keyboard-arrow-right] h-5 w-5" />} */}
                  <span className="icon-[ic--round-keyboard-arrow-right] h-5 w-5" />
                </Link>
              )}
            </MenuItem>

            <MenuItem>
              {({ focus }) => (
                <Link
                  to={PATH_NOTIFICATIONS.ROOT}
                  className={clsx(
                    "flex items-center justify-between py-3 px-2 rounded-md w-full text-sm font-medium hover:bg-gray-100 cursor-pointer relative",
                    focus ? "bg-gray-100" : "dark:text-white"
                  )}
                >
                  <span className="flex-1">{t("nav_menu_notifications")}</span>
                  {totalUnRead > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white mr-2">
                      {totalUnRead > 99 ? "99+" : totalUnRead}
                    </span>
                  )}
                  <span className="icon-[ic--round-keyboard-arrow-right] h-5 w-5" />
                </Link>
              )}
            </MenuItem>

            <LanguageSelectionMenu />

            {/* Debug Environment Variables Display - Only visible in debug mode */}
            {environment.debugMode && (
              <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-600">
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  <div>NODE_ENV: <span className="font-mono">{process.env.NODE_ENV || 'undefined'}</span></div>
                  <div>REACT_APP_LOCAL_DEV: <span className="font-mono">{process.env.REACT_APP_LOCAL_DEV || 'undefined'}</span></div>
                  <div>REACT_APP_MRR_ENVIRONMENT: <span className="font-mono">{process.env.REACT_APP_MRR_ENVIRONMENT || 'undefined'}</span></div>
                  <div>REACT_APP_HOSTNAME: <span className="font-mono">{process.env.REACT_APP_HOSTNAME || 'undefined'}</span></div>
                  <div className="pt-1 border-t border-gray-100 dark:border-gray-700">
                    <div>Debug Mode: <span className="font-mono font-semibold">{environment.debugMode ? 'TRUE' : 'FALSE'}</span></div>
                    <div>Dev Mode: <span className="font-mono font-semibold">{environment.debugMode ? 'TRUE' : 'FALSE'}</span></div>
                  </div>
                </div>
              </div>
            )}

            {/* Debug Menu Item - Only visible in debug/dev mode */}
            {environment.debugMode && (
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={() => setDebugModalOpen(true)}
                    className={clsx(
                      "flex items-center justify-between py-3 px-2 rounded-md w-full text-sm font-medium hover:bg-gray-100 cursor-pointer",
                      focus ? "bg-gray-100" : "dark:text-white"
                    )}
                  >
                    <span>Debug Info</span>
                    <span className="icon-[ic--round-keyboard-arrow-right] h-5 w-5" />
                  </button>
                )}
              </MenuItem>
            )}

            {environment.debugMode && (
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={() => navigate('/dev/brand')}
                    className={clsx(
                      "flex items-center justify-between py-3 px-2 rounded-md w-full text-sm font-medium hover:bg-gray-100 cursor-pointer",
                      focus ? "bg-gray-100" : "dark:text-white"
                    )}
                  >
                    <div className="flex flex-col items-start">
                      <span>Brand Selector</span>
                      {currentBrand && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Current: {currentBrand.code}
                        </span>
                      )}
                    </div>
                    <span className="icon-[fluent--building-multiple-20-filled] h-5 w-5" />
                  </button>
                )}
              </MenuItem>
            )}

            <MenuItem>
              {({ focus }) => (
                <Field
                  as="div"
                  className={clsx("flex items-center justify-between p-2")}
                >
                  <Label
                    as="span"
                    className="mr-3 text-sm font-medium text-gray-700 dark:text-white"
                  >
                    {t("user_page_menu_dark_mode")}
                  </Label>
                  <Switch
                    checked={userSettings.dark_mode}
                    onChange={async (dark) => {
                      document.documentElement.classList.toggle("dark", dark);
                      document.documentElement.classList.toggle(
                        "tw-dark",
                        dark
                      );
                      setUserSettings((prev) => ({
                        ...prev,
                        dark_mode: dark,
                      }));
                      await postUserSettingsByUsername(user.sf_user_id, {
                        ...userSettings,
                        dark_mode: dark,
                      });
                    }}
                    className={clsx(
                      userSettings.dark_mode
                        ? "bg-primary-main"
                        : "bg-gray-200",
                      "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-main focus:ring-offset-2"
                    )}
                  >
                    {/* LOC: ignore SR ONLY */}
                    <span className="sr-only">Use setting</span>
                    <span
                      className={clsx(
                        userSettings.dark_mode
                          ? "translate-x-5"
                          : "translate-x-0",
                        "pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                      )}
                    >
                      <span
                        className={clsx(
                          userSettings.dark_mode
                            ? "opacity-0 duration-100 ease-out"
                            : "opacity-100 duration-200 ease-in",
                          "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity"
                        )}
                        aria-hidden="true"
                      >
                        <span className="icon-[line-md--sunny-filled-loop]" />
                      </span>
                      <span
                        className={clsx(
                          userSettings.dark_mode
                            ? "opacity-100 duration-200 ease-in"
                            : "opacity-0 duration-100 ease-out",
                          "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity"
                        )}
                        aria-hidden="true"
                      >
                        <span className="icon-[line-md--sunny-outline-to-moon-loop-transition]" />
                      </span>
                    </span>
                  </Switch>
                </Field>
              )}
            </MenuItem>
            <MenuItem>
              {({ focus }) => (
                <div className="p-2">
                  <button
                    type="button"
                    onClick={async () => {
                      await logout();
                    }}
                    className={clsx(
                      focus ? "bg-error-main dark:text-white" : "",
                      "bg-error-dark rounded block px-4 py-2 text-sm text-white w-full"
                    )}
                  >
                    {t("user_page_log_out")}
                  </button>
                </div>
              )}
            </MenuItem>
          </div>
        </MenuItems>
      </MenuSection>

      {/* Debug Modal */}
      <DebugModal open={debugModalOpen} setOpen={setDebugModalOpen} />
    </Menu>
  );
};

export default ProfileMenu;
